import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import { FieldMapping } from '../../../types/import.types';

interface FieldMappingStepProps {
  headers: string[];
  preview: any[];
  onComplete: (mapping: FieldMapping) => void;
  onBack: () => void;
}

// Available lead fields for mapping
const LEAD_FIELDS = [
  { value: 'first_name', label: 'First Name', required: true },
  { value: 'last_name', label: 'Last Name', required: true },
  { value: 'email', label: 'Email', required: false },
  { value: 'phone', label: 'Phone', required: false },
  { value: 'company', label: 'Company', required: false },
  { value: 'position', label: 'Position/Title', required: false },
  { value: 'website', label: 'Website', required: false },
  { value: 'status', label: 'Status', required: false },
  { value: 'priority', label: 'Priority', required: false },
  { value: 'source', label: 'Source', required: false },
  { value: 'notes', label: 'Notes', required: false },
  { value: 'address_line1', label: 'Address Line 1', required: false },
  { value: 'address_line2', label: 'Address Line 2', required: false },
  { value: 'city', label: 'City', required: false },
  { value: 'state', label: 'State/Province', required: false },
  { value: 'country', label: 'Country', required: false },
  { value: 'postal_code', label: 'Postal Code', required: false },
  { value: 'estimated_value', label: 'Estimated Value', required: false },
];

export const FieldMappingStep: React.FC<FieldMappingStepProps> = ({
  headers,
  preview,
  onComplete,
  onBack,
}) => {
  const [mapping, setMapping] = useState<FieldMapping>({});
  const [errors, setErrors] = useState<string[]>([]);

  // Auto-map fields based on header names
  useEffect(() => {
    const autoMapping: FieldMapping = {};
    
    headers.forEach((header) => {
      const normalizedHeader = header.toLowerCase().replace(/[_\s-]/g, '');
      
      // Try to find matching field
      const matchedField = LEAD_FIELDS.find((field) => {
        const normalizedFieldName = field.value.toLowerCase().replace(/[_\s-]/g, '');
        return normalizedHeader.includes(normalizedFieldName) || 
               normalizedFieldName.includes(normalizedHeader);
      });
      
      if (matchedField) {
        autoMapping[header] = matchedField.value;
      }
    });
    
    setMapping(autoMapping);
  }, [headers]);

  const handleMappingChange = (csvColumn: string, leadField: string) => {
    setMapping((prev) => {
      const newMapping = { ...prev };
      if (leadField === '') {
        delete newMapping[csvColumn];
      } else {
        newMapping[csvColumn] = leadField;
      }
      return newMapping;
    });
  };

  const validateMapping = (): boolean => {
    const newErrors: string[] = [];
    const mappedFields = Object.values(mapping);
    
    // Check required fields
    const requiredFields = LEAD_FIELDS.filter((f) => f.required);
    requiredFields.forEach((field) => {
      if (!mappedFields.includes(field.value)) {
        newErrors.push(`Required field "${field.label}" is not mapped`);
      }
    });
    
    // Check if at least email or phone is mapped
    if (!mappedFields.includes('email') && !mappedFields.includes('phone')) {
      newErrors.push('At least one of Email or Phone must be mapped');
    }
    
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleContinue = () => {
    if (validateMapping()) {
      onComplete(mapping);
    }
  };

  const getMappedFieldLabel = (fieldValue: string): string => {
    const field = LEAD_FIELDS.find((f) => f.value === fieldValue);
    return field ? field.label : fieldValue;
  };

  const isFieldMapped = (fieldValue: string): boolean => {
    return Object.values(mapping).includes(fieldValue);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Map CSV Columns to Lead Fields
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Match your CSV columns to the corresponding lead fields. Required fields are marked with *.
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Please fix the following issues:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {headers.map((header) => (
            <Grid item xs={12} md={6} key={header}>
              <FormControl fullWidth>
                <InputLabel>{header}</InputLabel>
                <Select
                  value={mapping[header] || ''}
                  label={header}
                  onChange={(e) => handleMappingChange(header, e.target.value)}
                >
                  <MenuItem value="">
                    <em>Don't import</em>
                  </MenuItem>
                  {LEAD_FIELDS.map((field) => (
                    <MenuItem
                      key={field.value}
                      value={field.value}
                      disabled={isFieldMapped(field.value) && mapping[header] !== field.value}
                    >
                      {field.label}
                      {field.required && ' *'}
                      {isFieldMapped(field.value) && mapping[header] !== field.value && ' (mapped)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Preview of mapped data */}
      <Typography variant="h6" gutterBottom>
        Data Preview
      </Typography>
      
      <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {Object.entries(mapping).map(([csvColumn, leadField]) => (
                <TableCell key={csvColumn}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {csvColumn}
                    </Typography>
                    <Chip
                      label={getMappedFieldLabel(leadField)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.slice(0, 5).map((row, index) => (
              <TableRow key={index}>
                {Object.entries(mapping).map(([csvColumn]) => (
                  <TableCell key={csvColumn}>
                    {row[csvColumn] || '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {preview.length > 5 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Showing 5 of {preview.length} rows
        </Typography>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack} variant="outlined">
          Back
        </Button>
        <Button
          onClick={handleContinue}
          variant="contained"
          color="primary"
        >
          Continue to Validation
        </Button>
      </Box>
    </Box>
  );
};
